import booleanContains from '@turf/boolean-contains'
import type { FeatureCollection } from 'geojson'
import { useEffect, useMemo, useState } from 'react'
import type { LayerProps } from 'react-map-gl'
import { Layer, Source } from 'react-map-gl'
import {
  aClient,
  aDrawnFeature,
  aMapViewState,
  aSelectedListing,
  useAtomValue,
} from '~/atoms'
import { CLUSTER_ZOOM_LEVEL } from '~/constants'
import {
  MapView,
  useCurrentMapView,
  useListingPointLayerStyle,
} from '~/hooks/map'
import type { Listing } from '~/models'

export default function MapPropertyClustersLayer({
  properties,
}: {
  properties: Listing[]
}) {
  const mapViewState = useAtomValue(aMapViewState)
  const currentMapView = useCurrentMapView()
  const drawnFeature = useAtomValue(aDrawnFeature)
  const [shouldRenderComponent, setShouldRenderComponent] = useState(false)
  const selectedListing = useAtomValue(aSelectedListing)
  const client = useAtomValue(aClient)
  const unclusteredPointLayer = useListingPointLayerStyle({
    id: 'property-single-clusters',
    filter: ['!', ['has', 'point_count']],
  })

  const featuredIds = client!.featured?.map((f) => f.id) ?? []

  const selectedListingPoint = useMemo(() => {
    if (!selectedListing) {
      return undefined
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            ...selectedListing.location,
            type: 'Point',
          },
          properties: {
            ...selectedListing,
          },
        },
      ],
    } as FeatureCollection
  }, [selectedListing])

  useEffect(() => {
    if (!shouldRenderComponent) {
      if (currentMapView !== MapView.Map) {
        setShouldRenderComponent(false)
      } else if (mapViewState) {
        setShouldRenderComponent(true)
      }
    }
  }, [mapViewState, currentMapView, shouldRenderComponent])

  const points: FeatureCollection = useMemo(() => {
    let features = properties.filter((l) => l.location)
    if (drawnFeature) {
      features = features.filter((l: any) =>
        booleanContains(drawnFeature.geometry, l.location)
      )
    }

    return {
      type: 'FeatureCollection',
      features: features.map((l: any) => {
        return {
          type: 'Feature',
          geometry: {
            ...l.location,
            type: 'Point',
          },
          properties: {
            _id: l._id,
            slug: l.slug,
            status: l.status,
            address: l.property
              ? l.property.address2
                ? `${l.property.address} ${l.property.address2}`
                : l.property.address
              : null,
            type: l.type,
            imageType:
              l.type === 'sale/lease' || l.type === 'sale_lease'
                ? 'sale-lease'
                : l.type,
            name: l.name,
            lng: l.location.coordinates[0],
            lat: l.location.coordinates[1],
            primaryPropertyType:
              l.property_types && l.property_types.length > 0
                ? l.property_types[0]
                : 'Other',
            property_types: l.property_types,
            featured: featuredIds.includes(l._id),
          },
        }
      }),
    }
  }, [properties, drawnFeature])

  const clusterCountLayer: LayerProps = {
    id: 'property-clusters-count',
    type: 'symbol',
    source: 'property-listings',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['Roboto Medium'],
      'text-size': 12,
      'text-offset': [0, 0.15],
      'text-allow-overlap': true,
      'text-ignore-placement': true,
    },
    paint: {
      'text-color': '#ffffff',
    },
  }

  const clusterLayer: LayerProps = {
    type: 'circle',
    id: 'property-clusters',
    source: 'property-clusters',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': '#0284c7',
      'circle-radius': 18,
      'circle-stroke-color': '#0284c7',
      'circle-stroke-opacity': 0.25,
      'circle-stroke-width': 5,
    },
  }

  const selectedPoint: LayerProps = {
    id: 'selected-pointer-layer',
    type: 'symbol',
    layout: {
      'icon-image': [
        'case',
        ['==', ['get', 'featured'], true],
        'featured-selected',
        [
          'match',
          ['get', 'type'],
          'sale',
          'selected-sale',
          'lease',
          'selected-lease',
          'selected-sale-lease',
        ],
      ],
      'icon-size': ['case', ['==', ['get', 'featured'], true], 0.25, 0.2],
      'icon-allow-overlap': true,
    },
  }

  if (!shouldRenderComponent) {
    return null
  }

  return (
    <>
      <Source
        id="property-listings"
        type="geojson"
        data={points}
        cluster={true}
        clusterMaxZoom={CLUSTER_ZOOM_LEVEL}>
        <Layer {...clusterLayer} />
        <Layer {...clusterCountLayer} />
        <Layer {...unclusteredPointLayer} />
      </Source>
      {selectedListing && (
        <Source type="geojson" data={selectedListingPoint}>
          <Layer {...selectedPoint} />
        </Source>
      )}
    </>
  )
}
